import { useState, useEffect, useRef } from 'react'
import { ThinDelivery } from '../../../types'

type GroupingLogicProps = {
  id: number
  delivery?: ThinDelivery[]
  orientation: 'vertical' | 'horizontal'
}

export const useGroupingLogic = ({
  id,
  delivery,
  orientation,
}: GroupingLogicProps) => {
  const [groupedDeliveries, setGroupedDeliveries] = useState<
    Record<number, number[]>
  >({})

  useEffect(() => {
    if (delivery) {
      const updatedGroups = delivery.reduce<Record<number, number[]>>(
        (acc, item) => {
          if (item.delivery_group?.id) {
            if (!acc[item.delivery_group.id]) acc[item.delivery_group.id] = []
            acc[item.delivery_group.id].push(item.id)
          }
          return acc
        },
        {}
      )

      setGroupedDeliveries(updatedGroups)
    }
  }, [delivery])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [lineStyles, setLineStyles] = useState<
    Record<number, React.CSSProperties>
  >({})

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()

    const updatedStyles: Record<number, React.CSSProperties> = {}

    Object.entries(groupedDeliveries).forEach(([groupId, ids]) => {
      const groupedElements = ids
        .map((detailId) =>
          container.querySelector(
            `[data-divider-id="divider-${id}-${detailId}"]`
          )
        )
        .filter(Boolean) as HTMLElement[]

      if (groupedElements.length > 0) {
        const firstElement = groupedElements[0]
        const lastElement = groupedElements[groupedElements.length - 1]

        const firstRect = firstElement.getBoundingClientRect()
        const lastRect = lastElement.getBoundingClientRect()

        if (orientation === 'vertical') {
          const top = firstRect.top - containerRect.top + container.scrollTop
          const height = lastRect.bottom - firstRect.top

          updatedStyles[Number(groupId)] = { top, height }
        } else {
          const left =
            firstRect.left - containerRect.left + container.scrollLeft
          const width = lastRect.right - firstRect.left

          updatedStyles[Number(groupId)] = { left, width }
        }
      }
    })

    setLineStyles(updatedStyles)
  }, [groupedDeliveries, delivery, id, orientation])

  return { groupedDeliveries, setGroupedDeliveries, containerRef, lineStyles }
}
